import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, Loader2, PackageSearch } from 'lucide-react';

import { useViewModelReady } from '../../../../hooks/useViewModel';
import { NEXUS_BUG_LIMITS, type NexusBugInput, type NexusBugListRow } from '../../../../domain/nexus/model';
import { BugReportsSection } from './BugReportsSection';
import { useBugEditorViewModel, useBugListViewModel } from './bugsViewModels';
import { bugEditorSearch, drillDownSearch } from './deliveredTaskHelpers';
import { NexusErrorBanner } from './ErrorBanner';
import { formatNexusTimestamp } from './helpers';

const BUG_ID_RE = /^\d{1,20}$/;

/** Project details for each delivery bug. `?bug=` opens one bug's form. */
export function BugsPanel() {
  const [searchParams] = useSearchParams();
  const bug = searchParams.get('bug');
  if (bug && BUG_ID_RE.test(bug)) return <BugEditor key={bug} bugId={bug} />;
  return <BugList />;
}

function Breadcrumbs({ bugId }: { bugId?: string }) {
  return (
    <nav aria-label="Bugs" className="mt-4 flex flex-wrap items-center gap-1 text-sm">
      {bugId ? (
        <>
          <Link to={{ search: '?tab=bugs' }} className="text-primary hover:underline">All bugs</Link>
          <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
          <span className="font-medium text-gray-900" aria-current="page">Bug {bugId}</span>
        </>
      ) : (
        <span className="font-medium text-gray-900" aria-current="page">All bugs</span>
      )}
    </nav>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// --- List ---------------------------------------------------------------------

function BugList() {
  const vm = useBugListViewModel();
  useViewModelReady(vm.initialize);
  const bugs = vm.data?.bugs ?? [];
  return (
    <>
      <Breadcrumbs />
      <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Bugs</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Project details for each Nexus delivery bug. The project name and owner appear under the bug ID on Delivered Tasks.
          </p>
        </div>
        <AddBugForm />
      </div>
      {vm.busy ? <Spinner /> : vm.error ? <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} /> : bugs.length ? (
        <section className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3 font-medium sm:pl-5">Bug</th>
                  <th className="px-3 py-3 font-medium">Project name</th>
                  <th className="px-3 py-3 font-medium">Project owner</th>
                  <th className="px-3 py-3 font-medium">Description</th>
                  <th className="px-3 py-3 text-right font-medium">Tasks</th>
                  <th className="px-3 py-3 font-medium sm:pr-5">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bugs.map((bug) => <BugListRow key={bug.deliveryBugId} bug={bug} />)}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="mt-4 flex flex-col items-center rounded-md border border-gray-200 bg-white px-5 py-10 text-center text-gray-400">
          <PackageSearch className="h-6 w-6" />
          <p className="mt-2 text-sm font-medium text-gray-700">No bugs yet. Add one by ID.</p>
        </div>
      )}
    </>
  );
}

function AddBugForm() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const bugId = value.trim();
  const valid = BUG_ID_RE.test(bugId);
  return (
    <form
      className="flex shrink-0 items-start gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) navigate({ search: bugEditorSearch(bugId) });
      }}
    >
      <label className="block">
        <span className="sr-only">Bug ID to add or edit</span>
        <input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          inputMode="numeric"
          placeholder="Bug ID, e.g. 573208747"
          aria-invalid={Boolean(bugId) && !valid}
          className="h-9 w-56 rounded-md border border-gray-300 bg-white px-3 font-mono text-sm placeholder:font-sans placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {bugId && !valid && <span className="mt-1 block text-xs text-red-700">A bug ID is digits only.</span>}
      </label>
      <button
        type="submit"
        disabled={!valid}
        className="inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-md bg-primary px-3 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
      >
        Add or edit
      </button>
    </form>
  );
}

function BugListRow({ bug }: { bug: NexusBugListRow }) {
  const hasDetails = Boolean(bug.projectName || bug.projectOwner || bug.description);
  return (
    <tr className="align-top hover:bg-gray-50">
      <td className="px-3 py-3 sm:pl-5">
        <Link to={{ search: bugEditorSearch(bug.deliveryBugId) }} className="font-mono text-sm font-medium text-primary hover:underline">
          {bug.deliveryBugId}
        </Link>
        {bug.deliveryNames.length > 0 && <p className="mt-0.5 max-w-[14rem] text-[11px] text-gray-500">{bug.deliveryNames.join(', ')}</p>}
      </td>
      {hasDetails ? (
        <>
          <td className="px-3 py-3 text-sm text-gray-900">{bug.projectName || <span className="text-gray-400">—</span>}</td>
          <td className="px-3 py-3 text-sm text-gray-900">{bug.projectOwner || <span className="text-gray-400">—</span>}</td>
          <td className="max-w-md px-3 py-3 text-xs text-gray-600">
            <p className="line-clamp-2 whitespace-pre-line">{bug.description || <span className="text-gray-400">—</span>}</p>
          </td>
        </>
      ) : (
        <td colSpan={3} className="px-3 py-3 text-xs">
          <Link to={{ search: bugEditorSearch(bug.deliveryBugId) }} className="text-primary hover:underline">Add project details</Link>
        </td>
      )}
      <td className="px-3 py-3 text-right font-mono text-xs text-gray-900">{bug.taskCount.toLocaleString()}</td>
      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500 sm:pr-5">{bug.updatedAt ? formatNexusTimestamp(bug.updatedAt) : '—'}</td>
    </tr>
  );
}

// --- Editor -------------------------------------------------------------------

const FIELDS: { field: keyof NexusBugInput; label: string; multiline?: boolean; placeholder: string }[] = [
  { field: 'projectName', label: 'Project name', placeholder: 'e.g. Honeydew' },
  { field: 'projectOwner', label: 'Project owner', placeholder: 'e.g. Paul McDonald' },
  { field: 'description', label: 'Description', multiline: true, placeholder: 'What this bug covers, links, and anything reviewers should know' },
];

function BugEditor({ bugId }: { bugId: string }) {
  const vm = useBugEditorViewModel(bugId);
  useViewModelReady(vm.initialize);
  const hasErrors = Object.keys(vm.errors).length > 0;
  return (
    <>
      <Breadcrumbs bugId={bugId} />
      <div className="mt-3 flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Bug <span className="font-mono">{bugId}</span></h2>
          {vm.editor && (
            <p className="mt-0.5 text-sm text-gray-500">
              {vm.editor.projectCount
                ? <>{vm.editor.projectCount.toLocaleString()} Nexus project{vm.editor.projectCount === 1 ? '' : 's'}{vm.editor.deliveryNames.length ? ` · ${vm.editor.deliveryNames.join(', ')}` : ''}</>
                : 'No deliveries recorded for this bug yet.'}
            </p>
          )}
        </div>
        {vm.editor?.projectCount ? (
          <Link to={{ search: drillDownSearch({ bug: bugId }) }} className="text-sm font-medium text-primary hover:underline">
            View delivered tasks
          </Link>
        ) : null}
      </div>

      {vm.error && <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />}

      {vm.busy ? <Spinner /> : vm.editor && (
        <form
          className="mt-4 max-w-2xl space-y-4 rounded-md border border-gray-200 bg-white p-4 sm:p-5"
          onSubmit={(event) => {
            event.preventDefault();
            void vm.save();
          }}
        >
          {FIELDS.map(({ field, label, multiline, placeholder }) => (
            <Field
              key={field}
              id={`nexus-bug-${field}`}
              label={label}
              value={vm.form[field]}
              max={NEXUS_BUG_LIMITS[field]}
              error={vm.errors[field]}
              multiline={multiline}
              placeholder={placeholder}
              onChange={(value) => vm.setField(field, value)}
            />
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={!vm.dirty || hasErrors || vm.saving}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {vm.saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
            <button
              type="button"
              onClick={vm.reset}
              disabled={!vm.dirty || vm.saving}
              className="h-9 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Discard changes
            </button>
            {vm.savedAt && !vm.dirty && (
              <span role="status" className="inline-flex items-center gap-1 text-sm text-emerald-700">
                <Check className="h-4 w-4" /> Saved
              </span>
            )}
            {vm.editor.bug.updatedAt && !vm.savedAt && (
              <span className="text-xs text-gray-500">Last updated {formatNexusTimestamp(vm.editor.bug.updatedAt)}</span>
            )}
          </div>
        </form>
      )}

      {!vm.busy && vm.editor && <BugReportsSection bug={vm.editor.bug} onSent={vm.applyReport} />}
    </>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  max: number;
  error?: string;
  multiline?: boolean;
  placeholder: string;
  onChange: (value: string) => void;
}

function Field({ id, label, value, max, error, multiline, placeholder, onChange }: FieldProps) {
  const className = `w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
    error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary'
  }`;
  const describedBy = `${id}-help`;
  const shared = {
    id, value, placeholder, className,
    'aria-invalid': Boolean(error),
    'aria-describedby': describedBy,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(event.target.value),
  };
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1">{multiline ? <textarea rows={5} {...shared} /> : <input type="text" {...shared} />}</div>
      <p id={describedBy} className={`mt-1 text-xs ${error ? 'text-red-700' : 'text-gray-400'}`}>
        {error || `${value.trim().length.toLocaleString()} / ${max.toLocaleString()}`}
      </p>
    </div>
  );
}
